import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router";
import classNames from "classnames";
import AdminAPI from "../../../api/AdminAPI";
import { AdminHeader } from "../../../components/adminHeader/adminHeader";
import { Button } from "../../../components/button/button";
import { NumberInput } from "../../../components/numberInput/numberInput";
import { DeleteIcon, PlusIcon } from "../../../assets/icons";
import { usePopup } from "../../../components/popup/popup";
import styles from "./CafeMotivationPage.module.css";

import { Text } from "../../../components/text/text";
import { Separator } from "../../../components/separator/separator";

export const CafeMotivationPage = () => {
    const { cafeId } = useParams();
    const [conditions, setConditions] = useState([]);
    const [saving, setSaving] = useState(false);

    const [planPercent, setPlanPercent] = useState("0");
    const [revenuePercent, setRevenuePercent] = useState("0");
    const [planError, setPlanError] = useState(false);
    const [revenueError, setRevenueError] = useState(false);
    const [duplicateWarning, setDuplicateWarning] = useState(false);

    const [renderPopup, showPopup, hidePopup] = usePopup();
    const preEditRef = useRef({});

    useEffect(() => {
        if (!cafeId) return;
        AdminAPI.fetchCafeMotivations(cafeId).then(setConditions).catch(console.error);
    }, [cafeId]);

    const openPopup = () => {
        const initialPlan = "0";
        const isDuplicate = conditions.some(c => +c.percentageOfPlan === +initialPlan);
        setPlanPercent(initialPlan);
        setRevenuePercent("0");
        setPlanError(isDuplicate);
        setRevenueError(false);
        setDuplicateWarning(isDuplicate);
        showPopup();
    };

    const handlePlanChange = (v) => {
        setPlanPercent(v);
        const isDuplicate = conditions.some(c => +c.percentageOfPlan === +v);
        setDuplicateWarning(isDuplicate);
        setPlanError(isDuplicate || +v > 100);
    };

    const handleRevenueChange = (v) => {
        setRevenuePercent(v);
        setRevenueError(+v > 100);
    };

    const handleAdd = () => {
        let hasError = false;
        if (+planPercent > 100 || duplicateWarning) { setPlanError(true); hasError = true; }
        if (+revenuePercent > 100) { setRevenueError(true); hasError = true; }
        if (hasError) return;

        setSaving(true);
        AdminAPI
            .createCafeMotivation(+cafeId, {
                description: `план ${planPercent}% → бонус ${revenuePercent}%`,
                percentageOfPlan: +planPercent,
                employeeRevenue: +revenuePercent,
            })
            .then(created => {
                setConditions(prev => [...prev, created]);
                hidePopup();
            })
            .catch(console.error)
            .finally(() => setSaving(false));
    };

    const updateField = (id, field, value) => {
        setConditions(prev => prev.map(c => c.id === id ? { ...c, [field]: value } : c));
    };

    const handleFocusCapture = (condition) => {
        preEditRef.current[condition.id] = { ...condition };
    };

    const handleBlurSave = (condition) => {
        AdminAPI
            .updateCafeMotivation(condition.id, {
                cafeId: condition.cafeId,
                description: `план ${condition.percentageOfPlan}% → бонус ${condition.employeeRevenue}%`,
                percentageOfPlan: +condition.percentageOfPlan,
                employeeRevenue: +condition.employeeRevenue,
            })
            .catch((err) => {
                console.error(err);
                const prev = preEditRef.current[condition.id];
                if (prev) {
                    setConditions(prevConds =>
                        prevConds.map(c => c.id === condition.id ? prev : c)
                    );
                }
            });
    };

    const handleDelete = (id) => {
        const condition = conditions.find(c => c.id === id);
        AdminAPI
            .deleteCafeMotivation(id, condition?.cafeId ?? +cafeId)
            .then(() => setConditions(prev => prev.filter(c => c.id !== id)))
            .catch(console.error);
    };

    return (
        <>
            {renderPopup({
                children: (
                    <div className={styles.popup_content}>
                        <Text as="div" variant="h1" className={styles.popup_title} >Новое условие</Text>

                        <div className={classNames(styles.popup_field, { [styles.error]: planError })}>
                            <Text as="div" variant="caption" className={styles.field_label} >% выполнения плана</Text>
                            <NumberInput
                                val={planPercent}
                                setVal={handlePlanChange}
                                error={planError}
                                type="float"
                                max={100}
                            />
                            {duplicateWarning && (
                                <Text as="div" variant="body" className={styles.warning} size={12}>Правило с таким % плана уже существует</Text>
                            )}
                        </div>

                        <div className={classNames(styles.popup_field, { [styles.error]: revenueError })}>
                            <Text as="div" variant="caption" className={styles.field_label} >% от выручки сотруднику</Text>
                            <NumberInput
                                val={revenuePercent}
                                setVal={handleRevenueChange}
                                error={revenueError}
                                type="float"
                                max={100}
                            />
                        </div>

                        <Button
                            className={styles.popup_btn}
                            text="Добавить"
                            onClick={handleAdd}
                            disabled={saving}
                        />
                    </div>
                )
            })}

            <AdminHeader title="Мотивация" />
            <Text as="div" variant="body" className="page_description" >
                Условия бонуса: при выполнении плана на n% сотрудник получает m% от выручки.
            </Text>

            {conditions.length > 0 && (
                <div className={styles.list_header}>
                    <span>% выполнения плана</span>
                    <span>% от выручки</span>
                </div>
            )}

            <div className={styles.list}>
                {conditions.map(c => (
                    <div key={c.id} className={styles.condition_card}>
                        <div className={styles.card_fields}>
                            <div className={styles.card_field}>
                                <NumberInput
                                    val={String(c.percentageOfPlan)}
                                    setVal={(v) => updateField(c.id, "percentageOfPlan", v)}
                                    onFocus={() => handleFocusCapture(c)}
                                    onBlur={() => handleBlurSave(c)}
                                    type="float"
                                    max={100}
                                />
                            </div>
                            <div className={styles.card_field}>
                                <NumberInput
                                    val={String(c.employeeRevenue)}
                                    setVal={(v) => updateField(c.id, "employeeRevenue", v)}
                                    onFocus={() => handleFocusCapture(c)}
                                    onBlur={() => handleBlurSave(c)}
                                    type="float"
                                    max={100}
                                />
                            </div>
                        </div>
                        <DeleteIcon onClick={() => handleDelete(c.id)} />
                    </div>
                ))}

                {conditions.length === 0 && (
                    <Text as="div" variant="body" className={styles.empty} >Условия не заданы</Text>
                )}
            </div>

            <Separator />

            <Button
                fixed
                text="Добавить мотивацию"
                icon={<PlusIcon />}
                onClick={openPopup}
            />
        </>
    );
}
